import * as React from 'react'
import { Box, Typography, Grid } from '@mui/material'
import EditSvg from '@assets/Analytics/Frame.svg'
import CopySvg from '@assets/map_header_icons/Copy.svg'
import CalcIcon from '@assets/Analytics/Group.svg'
import DataGridPremiumDemo from '@/components/Table/DataGrid'
import WaterTreesCountCard from '@/components/Analytics/WaterTreesCountCard'
import HeaderAnalytics from './HeaderAnalytics'
import WaterTreeEditorDialog from '@components/Analytics/WaterTreeEditorDialog'
import WaterTreeEditAction from '@/components/Analytics/WaterTreeEditAction'
import SendNotificationDialog from '@/components/notification-send/SendNotificationDialog'
import MessageIcon from '@assets/MessageIcon.svg'
import { Dayjs } from 'dayjs'
import columnsConfig from '@/components/features/config/columns/analytics/analytics.details.column.config'
import { useParams, useOutletContext } from 'react-router-dom'
import { useEffect, useMemo } from 'react'
import YearComparisonChart from '@/components/charts/YearComparisonChart'
import { useAppTheme } from '@/theme/ThemeContext'
import {
  getWaterTreeZoneChartStats,
  getWaterTreeZoneStats,
  getWaterTreeZonePeriods,
  WaterMeterRow,
  WaterTreeZoneStats,
  WaterTreeZonePeriod,
  PeriodGranularity,
  WaterTreeZoneType,
  createWaterTree,
  updateWaterTree,
  getWaterTreeZoneTypes,
} from '@/services/dashboardService'
import { transformWaterToChartPoints } from '@/utils/chartUtils'
import { PeriodUnit } from './filters/PeriodUnitSelect'
import { normalizeLossPercent } from '@/utils/analyticsFormat'
import { createLatestRequest } from '@/utils/latestRequest'
import { AnalyticsDetailsKind } from './AnalyticsDetailsLayout'

const PERIOD_MAP: Record<PeriodUnit, PeriodGranularity> = {
  weeks: 'WEEKLY',
  months: 'MONTHLY',
  years: 'YEARLY',
}

type StatCard = {
  key: string
  title: string
  value: number | string
  showcub?: boolean
  size: 'large' | 'small'
  row: 1 | 2
  order: number
  visible: boolean
}

const AnalyticsDetails: React.FC<{ kind: AnalyticsDetailsKind }> = ({
  kind,
}) => {
  const { consumerId } = useParams()
  const { treeName, zoneType, zoneTypeId, onTreeNameChange } =
    useOutletContext<{
      treeName: string
      zoneType?: string
      zoneTypeId?: string
      onTreeNameChange?: (name: string) => void
    }>()
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const [periods, setPeriods] = React.useState<WaterTreeZonePeriod[]>([])
  const [zoneStats, setZoneStats] = React.useState<WaterTreeZoneStats | null>(
    null
  )
  const [chartRows, setChartRows] = React.useState<WaterMeterRow[]>([])
  const [chartLoading, setChartLoading] = React.useState(true)
  const [tableLoading, setTableLoading] = React.useState(true)
  const [editorMode, setEditorMode] = React.useState<
    'edit' | 'duplicate' | null
  >(null)
  const [zoneTypes, setZoneTypes] = React.useState<WaterTreeZoneType[]>([])
  const [sendOpen, setSendOpen] = React.useState(false)

  const [from, setFrom] = React.useState<Dayjs | null>(null)
  const [to, setTo] = React.useState<Dayjs | null>(null)
  const [periodUnit, setPeriodUnit] = React.useState<PeriodUnit>('months')
  const [searchTick, setSearchTick] = React.useState(0)
  const [didSearch, setDidSearch] = React.useState(false)
  const detailsRequestId = React.useRef(0)

  const stats = useMemo(() => {
    const list = Array.isArray(periods) ? periods : []
    const lossValues = list
      .map(period => normalizeLossPercent(period.lossPercent))
      .filter((value): value is number => value !== null)
    const avgLoss =
      lossValues.length > 0
        ? Math.round(
            (lossValues.reduce((sum, value) => sum + value, 0) /
              lossValues.length +
              Number.EPSILON) *
              100
          ) / 100
        : 0
    return {
      min: zoneStats?.minConsumption ?? 0,
      max: zoneStats?.maxConsumption ?? 0,
      avgLoss,
      consumers: zoneStats?.consumers ?? 0,
      suppliers: zoneStats?.suppliers ?? 0,
    }
  }, [zoneStats, periods])

  const statsCards = useMemo<StatCard[]>(() => {
    const cards: StatCard[] = [
      {
        key: 'min',
        title: 'MIN',
        value: stats.min,
        showcub: true,
        size: 'large',
        row: 1,
        order: 2,
        visible: stats.min !== null && stats.min !== undefined,
      },
      {
        key: 'max',
        title: 'MAX',
        value: stats.max,
        showcub: true,
        size: 'large',
        row: 1,
        order: 1,
        visible: stats.max !== null && stats.max !== undefined,
      },
      {
        key: 'avgLoss',
        title: 'אחוז פחת ממוצע',
        value: `${stats.avgLoss.toLocaleString('he-IL', { maximumFractionDigits: 2 })}%`,
        size: 'small',
        row: 2,
        order: 3,
        visible: stats.avgLoss !== null && stats.avgLoss !== undefined,
      },
      {
        key: 'consumers',
        title: 'צרכנים',
        value: stats.consumers,
        size: 'small',
        row: 2,
        order: 2,
        visible: stats.consumers !== null && stats.consumers !== undefined,
      },
      {
        key: 'suppliers',
        title: 'מספקים',
        value: stats.suppliers,
        size: 'small',
        row: 2,
        order: 1,
        visible: stats.suppliers !== null && stats.suppliers !== undefined,
      },
    ]

    return cards.filter(card => card.visible)
  }, [stats])

  const topRowCards = useMemo(() => {
    return statsCards
      .filter(card => card.row === 1)
      .sort((a, b) => a.order - b.order)
  }, [statsCards])

  const bottomRowCards = useMemo(() => {
    return statsCards
      .filter(card => card.row === 2)
      .sort((a, b) => a.order - b.order)
  }, [statsCards])

  const handleSearch = (r: {
    from: Dayjs | null
    to: Dayjs | null
    unit: PeriodUnit
  }) => {
    setFrom(r.from)
    setTo(r.to)
    setPeriodUnit(r.unit)
    setSearchTick(t => t + 1)
    setDidSearch(true)
  }

  const clearFilters = () => {
    setFrom(null)
    setTo(null)
    setPeriodUnit('months')
    setSearchTick(t => t + 1)
    setDidSearch(false)
  }

  useEffect(() => {
    const accountId = localStorage.getItem('accountId')
    if (!accountId) return
    getWaterTreeZoneTypes(accountId)
      .then(setZoneTypes)
      .catch(err => console.error('[AnalyticsDetails] zone types fetch:', err))
  }, [])

  const handleUpdateTree = async (data: {
    name: string
    zoneTypeId: number | null
  }) => {
    const accountId = localStorage.getItem('accountId')
    if (!accountId || !consumerId) return
    await updateWaterTree(
      accountId,
      Number(consumerId),
      data,
      Number(zoneType) === 1 ? 1 : 2
    )
    onTreeNameChange?.(data.name)
    setEditorMode(null)
  }

  const handleDuplicateTree = async (data: {
    name: string
    zoneTypeId: number | null
    copyFromId?: number | null
  }) => {
    const accountId = localStorage.getItem('accountId')
    if (!accountId || !consumerId) return
    await createWaterTree(accountId, {
      ...data,
      copyFromId: Number(consumerId),
    })
    setEditorMode(null)
  }

  useEffect(() => {
    if (!consumerId) return
    const accountId = localStorage.getItem('accountId')
    if (!accountId) {
      setChartLoading(false)
      return
    }

    const range =
      from && to
        ? {
            startDate: from.format('YYYY-MM-DD'),
            endDate: to.format('YYYY-MM-DD'),
          }
        : {}

    const request = createLatestRequest(detailsRequestId)

    setChartLoading(true)
    setTableLoading(true)
    const zoneKind = kind === 'tree' ? 1 : 2

    getWaterTreeZoneChartStats(accountId, consumerId, {
      zoneType: zoneKind,
      ...range,
      signal: request.signal,
    })
      .then(rows => {
        if (request.isCurrent()) setChartRows(rows)
      })
      .catch(err => {
        if (request.isCurrent()) {
          console.error('[AnalyticsDetails] zone chart fetch:', err)
        }
      })
      .finally(() => {
        if (request.isCurrent()) setChartLoading(false)
      })

    getWaterTreeZoneStats(accountId, consumerId, {
      zoneType: zoneKind,
      ...range,
      signal: request.signal,
    })
      .then(result => {
        if (request.isCurrent()) setZoneStats(result)
      })
      .catch(err => {
        if (request.isCurrent()) {
          console.error('[AnalyticsDetails] zone stats fetch:', err)
        }
      })
    getWaterTreeZonePeriods(accountId, consumerId, {
      zoneType: zoneKind,
      period: PERIOD_MAP[periodUnit],
      ...range,
      signal: request.signal,
    })
      .then(({ rows }) => {
        if (request.isCurrent()) setPeriods(rows)
      })
      .catch(err => {
        if (request.isCurrent()) {
          console.error('[AnalyticsDetails] zone periods fetch:', err)
        }
      })
      .finally(() => {
        if (request.isCurrent()) setTableLoading(false)
      })

    return () => {
      request.cancel()
    }
  }, [consumerId, kind, searchTick]) // eslint-disable-line react-hooks/exhaustive-deps

  // Both zone types are groups in the Gateway, so :consumerId is a group id on
  // the tree and the group route alike.
  const zoneNumericId = consumerId ? Number(consumerId) : NaN

  // Distinct name (not `accountId`) — matches the guard pattern in
  // src/pages/Consumers.tsx: read the raw value first so a missing/invalid
  // accountId becomes NaN instead of a silent 0 (Number(null) === 0) that
  // could reach the dialog as if it were a real account id.
  const notificationAccountIdRaw = localStorage.getItem('accountId')
  const notificationAccountId = notificationAccountIdRaw
    ? Number(notificationAccountIdRaw)
    : NaN

  // Live on BOTH route kinds — guards only against a malformed id/account,
  // never against the audience kind (no disabled/coming-soon state).
  const sendDisabled = !zoneNumericId || !notificationAccountId

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        mt: 2,
        direction: 'rtl',
        height: { xs: 'auto', lg: 'calc(100dvh - 245px)' },
        minHeight: { xs: 0, lg: 'calc(100dvh - 245px)' },
        overflow: { xs: 'visible', lg: 'hidden' },
        pb: 1,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'minmax(460px, 1.55fr) minmax(300px, 0.95fr) 150px',
          },
          gap: { xs: 2, lg: 2.5 },
          alignItems: 'flex-start',
        }}
      >
        {/* RIGHT: Chart */}
        <Box
          sx={{
            flex: 1,
            width: '100%',
            minWidth: 0,
          }}
        >
          <YearComparisonChart
            legend={false}
            headerLayout="bar"
            showCompareLabel={false}
            headerTitle={`מספקים: ${treeName} ${stats.suppliers}`}
            height={220}
            loading={chartLoading}
            data={transformWaterToChartPoints(chartRows)}
          />
        </Box>

        {/* LEFT: Stats */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <img src={CalcIcon} alt="" style={{ width: 16 }} />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '14px',
                  color: isDarkMode ? '#BBD7FF' : '#003F61',
                  fontFamily: 'Rubik, sans-serif',
                }}
              >
                תוצאות חישובי פחת
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  cursor: sendDisabled ? 'not-allowed' : 'pointer',
                  opacity: sendDisabled ? 0.5 : 1,
                  bgcolor: isDarkMode ? 'rgba(187, 215, 255, 0.08)' : '#F1F8FE',
                  px: 1.25,
                  py: 0.65,
                  borderRadius: '8px',
                  '&:hover': sendDisabled
                    ? {}
                    : {
                        bgcolor: isDarkMode
                          ? 'rgba(187, 215, 255, 0.15)'
                          : '#E1EFFE',
                      },
                }}
                onClick={() => {
                  if (!sendDisabled) setSendOpen(true)
                }}
              >
                <img
                  src={MessageIcon}
                  alt=""
                  style={{
                    width: 13,
                    filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: isDarkMode ? '#BBD7FF' : '#003F61',
                  }}
                >
                  שליחת הודעה לתת־העץ
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {/* ROW 1: MIN + MAX */}
            <Grid container spacing={1.2}>
              {topRowCards.map(card => (
                <Grid key={card.key} size={{ xs: 6 }}>
                  <WaterTreesCountCard
                    title={card.title}
                    value={card.value}
                    showcub={card.showcub}
                    height={126}
                    fontSize="24px"
                    color={isDarkMode ? '#BBD7FF' : '#003F61'}
                  />
                </Grid>
              ))}
            </Grid>

            {/* ROW 2: % + consumers + suppliers */}
            <Grid container spacing={1.2}>
              {bottomRowCards.map(card => (
                <Grid key={card.key} size={{ xs: 4 }}>
                  <WaterTreesCountCard
                    title={card.title}
                    value={card.value}
                    height={74}
                    fontSize="18px"
                    color={isDarkMode ? '#BBD7FF' : '#003F61'}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            width: '100%',
            pt: 1.5,
          }}
        >
          <WaterTreeEditAction
            label="עריכת עץ מים"
            iconSrc={EditSvg}
            width="100%"
            height={42}
            onClick={() => setEditorMode('edit')}
            sx={{
              borderRadius: '4px',
              justifyContent: 'space-between',
              px: 1.5,
            }}
          />
          <WaterTreeEditAction
            label="שכפל קבוצה"
            iconSrc={CopySvg}
            width="100%"
            height={42}
            onClick={() => setEditorMode('duplicate')}
            sx={{
              borderRadius: '4px',
              justifyContent: 'space-between',
              px: 1.5,
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          flex: 1,
          minHeight: 0,
        }}
      >
        <HeaderAnalytics
          from={from}
          to={to}
          onChangeFrom={setFrom}
          onChangeTo={setTo}
          onSearch={handleSearch}
          onClear={clearFilters}
          hasActiveFilter={didSearch}
        />

        <Box
          sx={{
            flex: 1,
            minHeight: { xs: 260, lg: 300 },
            height: { xs: 260, lg: '100%' },
            maxHeight: 'none',
          }}
        >
          <DataGridPremiumDemo
            width="100%"
            columns={columnsConfig}
            rows={
              Array.isArray(periods)
                ? periods.map((p, i) => ({ id: i, ...p }))
                : []
            }
            hideFooter={true}
            headerPosition="bottom"
            pageSize={25}
            maxHeight="100%"
            loading={tableLoading}
          />
        </Box>
      </Box>

      <WaterTreeEditorDialog
        open={editorMode !== null}
        mode={editorMode === 'duplicate' ? 'create' : 'edit'}
        initial={
          editorMode === 'duplicate'
            ? {
                name: `${treeName} - עותק`,
                zoneTypeId: Number(zoneTypeId) || undefined,
                copyFromId: Number(consumerId),
              }
            : { name: treeName, zoneTypeId: Number(zoneTypeId) || undefined }
        }
        zoneTypes={zoneTypes}
        copyOptions={
          consumerId ? [{ id: Number(consumerId), name: treeName }] : []
        }
        onUpdate={handleUpdateTree}
        onCreate={handleDuplicateTree}
        onClose={() => setEditorMode(null)}
      />

      <SendNotificationDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        accountId={notificationAccountId}
        audience={{ kind: 'group', groupId: zoneNumericId }}
      />
    </Box>
  )
}

export default AnalyticsDetails
